import { Plugin } from 'ckeditor5'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { documentUploadAudioService } from 'src/api/document'
import { toWidget, WidgetToolbarRepository } from 'ckeditor5'
import URL from 'src/api/url'

import TextToSpeech from '@minicoursegenerator/text-to-speech'
import { insertSingleAudioBox } from '../../utils'

function extractTextFromViewElement(viewElement) {
  let text = ''

  if (!viewElement || !viewElement.getChildren) {
    return text
  }

  for (const child of viewElement.getChildren()) {
    if (child.is && child.is('$text')) {
      text += child.data + ' '
    } else if (child.is && child.is('element')) {
      const tag = child.name
      // Skip media elements
      if (!['audio', 'video', 'img', 'iframe', 'tts-button'].includes(tag)) {
        text += extractTextFromViewElement(child) + ' '
      }
    }
  }
  return text.trim()
}

function extractTextFromModelElement(modelElement) {
  let text = ''

  if (!modelElement || !modelElement.getChildren) {
    return text
  }

  for (const child of modelElement.getChildren()) {
    if (child.is && child.is('$text')) {
      text += child.data + ' '
    } else if (child.is && child.is('element')) {
      const tag = child.name
      // Skip media elements and TTS elements
      if (
        ![
          'audioPlayer',
          'videoPlayer',
          'imageBlock',
          'imageInline',
          'textToSpeech',
        ].includes(tag)
      ) {
        text += extractTextFromModelElement(child) + ' '
      }
    }
  }
  return text.trim()
}

export default class TextToSpeechPlugin extends Plugin {
  constructor(editor) {
    super(editor)
    this.mountedRoots = new Map() // Track mounted React roots for cleanup
    this.isUploading = false // Loading state tracker
  }

  init() {
    const editor = this.editor

    // textToSpeech model element schema
    editor.model.schema.register('textToSpeech', {
      isObject: true,
      allowWhere: '$block',
      allowIn: 'column',
      allowAttributes: ['text', 'src', 'type', 'id'],
    })

    this._defineConverters()

    // Single audio ekleme komutu
    editor.commands.add('insertSingleAudio', {
      execute: () => {
        this.openAudioPickerAndUpload(src => {
          editor.model.change(writer => {
            const tts = writer.createElement('textToSpeech', {
              type: 'single',
              src: src,
            })
            editor.model.insertContent(tts)
            writer.setSelection(tts, 'on')
          })
        })
      },
    })

    // Drag & drop ile audio dosyası bırakıldığında upload et
    editor.editing.view.document.on('drop', (evt, data) => {
      const dataTransfer =
        data.dataTransfer || (evt.data && evt.data.dataTransfer)
      if (dataTransfer && dataTransfer.files && dataTransfer.files.length > 0) {
        const file = dataTransfer.files[0]
        if (file.type && file.type.startsWith('audio/')) {
          evt.stop()
          data.preventDefault()

          this.showLoadingOverlay('')

          const formData = new FormData()
          formData.append('file', file)
          // Yeni eklemede id yok, sadece dosya gönder
          documentUploadAudioService(formData)
            .then(response => {
              const src = URL.CDN + response?.data?.url
              const id = response?.data?.id ?? null
              // Sadece response geldikten sonra model değişikliği yapılır
              editor.model.change(writer => {
                const ttsAttrs = {
                  type: 'single',
                  src: src,
                }
                if (id !== null) ttsAttrs.id = id
                const tts = writer.createElement('textToSpeech', ttsAttrs)
                editor.model.insertContent(tts)
                writer.setSelection(tts, 'on')
              })
              this.hideLoadingOverlay()
            })
            .catch(() => {
              this.hideLoadingOverlay()
              alert('Audio upload failed!')
            })
        }
      }
    })

    this._setupColumnObserver()

    // Change Audio - Improved version
    editor.commands.add('changeAudio', {
      execute: () => {
        const selection = editor.model.document.selection
        const ttsElement = selection.getSelectedElement()
        if (ttsElement && ttsElement.name === 'textToSpeech') {
          this.changeAudioForElement(ttsElement)
        }
      },
    })

    editor.on('ready', () => {
      const widgetToolbarRepository = editor.plugins.get(
        WidgetToolbarRepository,
      )
      widgetToolbarRepository.register('textToSpeechToolbar', {
        ariaLabel: 'Text to Speech Toolbar',
        items: ['changeAudio'],
        getRelatedElement: selection => {
          const viewElement = selection.getSelectedElement()
          if (
            viewElement &&
            viewElement.hasClass &&
            viewElement.hasClass('tts-widget-container')
          ) {
            return viewElement
          }
          return null
        },
      })
    })

    // Toolbar butonu
    editor.ui.componentFactory.add('changeAudio', locale => {
      const button = document.createElement('button')
      button.className = 'ck-button ck-button_with-text'
      // Flex hizalama
      button.style.display = 'flex'
      button.style.alignItems = 'center'
      button.style.gap = '4px'
      button.style.cursor = 'default'

      // Info iconu ve tooltip wrapper
      const infoWrapper = document.createElement('span')
      infoWrapper.style.position = 'relative'
      infoWrapper.style.display = 'inline-block'
      infoWrapper.style.marginRight = '8px'
      infoWrapper.style.verticalAlign = 'middle'

      // SVG info iconu
      const infoIcon = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg',
      )
      infoIcon.setAttribute('width', '18')
      infoIcon.setAttribute('height', '18')
      infoIcon.setAttribute('viewBox', '0 0 24 24')
      infoIcon.style.verticalAlign = 'middle'
      infoIcon.style.display = 'inline-block'
      infoIcon.style.width = '18px'
      infoIcon.style.height = '18px'
      infoIcon.style.padding = '0'
      infoIcon.style.margin = '0'
      infoIcon.innerHTML = `
        <circle cx="12" cy="12" r="10" stroke="#888" stroke-width="2" fill="none"/>
        <line x1="12" y1="8" x2="12" y2="8" stroke="#888" stroke-width="2" stroke-linecap="round"/>
        <line x1="12" y1="11" x2="12" y2="16" stroke="#888" stroke-width="2" stroke-linecap="round"/>
      `

      // Tooltip
      const tooltip = document.createElement('span')
      tooltip.innerHTML =
        'Supported formats: MP3 and WAV.<br>Maximum file size: 100 MB'
      tooltip.style.visibility = 'hidden'
      tooltip.style.background = '#333'
      tooltip.style.color = '#fff'
      tooltip.style.textAlign = 'center'
      tooltip.style.borderRadius = '4px'
      tooltip.style.padding = '4px 8px'
      tooltip.style.position = 'absolute'
      tooltip.style.zIndex = '1'
      tooltip.style.bottom = '125%'
      tooltip.style.left = '50%'
      tooltip.style.transform = 'translateX(-50%)'
      tooltip.style.whiteSpace = 'nowrap'
      tooltip.style.fontSize = '12px'

      // Hover eventleri
      infoIcon.onmouseenter = () => {
        tooltip.style.visibility = 'visible'
      }
      infoIcon.onmouseleave = () => {
        tooltip.style.visibility = 'hidden'
      }

      infoWrapper.appendChild(infoIcon)
      infoWrapper.appendChild(tooltip)

      // Butonun başına infoWrapper'ı ekle
      button.appendChild(infoWrapper)
      // Sonra buton metnini ekle - wrap in span for hover effect
      const textSpan = document.createElement('span')
      textSpan.className = 'change-audio-text'
      textSpan.textContent = 'Change Audio'
      textSpan.style.padding = '4px 8px'
      textSpan.style.borderRadius = '4px'
      textSpan.style.cursor = 'pointer'
      button.appendChild(textSpan)

      // Only trigger file upload when clicking on the text, not the info icon
      textSpan.onclick = () => {
        if (!this.isUploading) {
          editor.execute('changeAudio')
        }
      }
      return {
        element: button,
        render: () => {},
        destroy: () => {},
      }
    })

    // Inject hover styles for Change Audio button to match Block Options button
    const injectChangeAudioHoverStyles = () => {
      // Check if style already exists to avoid duplicates
      if (!document.getElementById('change-audio-hover-styles')) {
        const style = document.createElement('style')
        style.id = 'change-audio-hover-styles'
        style.textContent = `
          /* Ensure default cursor for the Change Audio button container */
          .ck-button.ck-button_with-text {
            cursor: default;
          }

          /* Change Audio text hover - matching Block Options button style */
          .ck-button.ck-button_with-text .change-audio-text {
            transition: background-color 0.2s;
            cursor: pointer;
          }

          .ck-button.ck-button_with-text .change-audio-text:hover {
            background-color: var(--ck-color-button-default-hover-background, #e6e6e6);
          }
        `
        document.head.appendChild(style)
      }
    }

    // Call it in init()
    injectChangeAudioHoverStyles()
  }

  // NEW: Loading overlay methods
  showLoadingOverlay(message = '') {
    this.isUploading = true

    // Create overlay if it doesn't exist
    let overlay = document.querySelector('.tts-loading-overlay')
    if (!overlay) {
      overlay = document.createElement('div')
      overlay.className = 'tts-loading-overlay'
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
      `

      const content = document.createElement('div')
      content.style.cssText = `
        background: white;
        padding: 20px 40px;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 15px;
      `

      const spinner = document.createElement('div')
      spinner.className = 'tts-spinner'
      spinner.style.cssText = `
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
      `

      const text = document.createElement('div')
      text.className = 'tts-loading-text'
      text.textContent = message
      text.style.cssText = `
        font-size: 16px;
        color: #333;
      `

      content.appendChild(spinner)
      content.appendChild(text)
      overlay.appendChild(content)

      // Add spinner animation
      const style = document.createElement('style')
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `
      document.head.appendChild(style)

      document.body.appendChild(overlay)
    }

    overlay.style.display = 'flex'
  }

  hideLoadingOverlay() {
    this.isUploading = false
    const overlay = document.querySelector('.tts-loading-overlay')
    if (overlay) {
      overlay.style.display = 'none'
    }
  }

  // NEW: Improved change audio method
  async changeAudioForElement(ttsElement) {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'audio/*'

    input.onchange = async e => {
      const file = e.target.files[0]
      if (!file) return

      this.showLoadingOverlay('')

      try {
        const formData = new FormData()
        formData.append('file', file)

        const id = ttsElement.getAttribute('id')
        if (id) {
          formData.append('id', id)
        }

        const response = await documentUploadAudioService(formData)

        if (response?.data?.url) {
          const newSrc = URL.CDN + response.data.url
          const newId = response?.data?.id ?? null

          // Get current position and attributes
          const text = ttsElement.getAttribute('text') || ''
          const type = ttsElement.getAttribute('type') || 'single'
          const parent = ttsElement.parent
          const index = ttsElement.index

          // Remove old element and insert new one to force re-render
          this.editor.model.change(writer => {
            // Create new element with updated src
            const newAttrs = {
              text: text,
              src: newSrc,
              type: type,
            }
            if (newId !== null) {
              newAttrs.id = newId
            }

            const newTts = writer.createElement('textToSpeech', newAttrs)

            // Remove old element
            writer.remove(ttsElement)

            // Insert new element at the same position
            writer.insert(newTts, parent, index)

            // Select the new element
            writer.setSelection(newTts, 'on')
          })

          this.hideLoadingOverlay()
        } else {
          throw new Error('Invalid response structure')
        }
      } catch (err) {
        console.error('Upload error:', err)
        this.hideLoadingOverlay()
        alert('Audio upload failed: ' + err.message)
      }
    }

    input.click()
  }

  openAudioPickerAndUpload(callback) {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'audio/*'
    input.onchange = async e => {
      const file = e.target.files[0]
      if (file) {
        this.showLoadingOverlay('')

        const currentPosition = window.editor.model.document.selection.getFirstPosition()

        const formData = new FormData()
        formData.append('file', file)
        let id = null
        const selection = window.editor.model.document.selection
        const ttsElement = selection.getSelectedElement()
        if (ttsElement && ttsElement.name === 'textToSpeech') {
          id = ttsElement.getAttribute('id')
        }
        if (id) {
          formData.append('id', id)
        }
        try {
          const response = await documentUploadAudioService(formData)

          if (response?.data?.url) {
            const src = URL.CDN + response.data.url
            const id = response?.data?.id ?? null
            insertSingleAudioBox(window.editor, { src, id }, currentPosition)
            this.hideLoadingOverlay()
          } else {
            console.error('Invalid response structure:', response)
            this.hideLoadingOverlay()
            alert('Upload response is invalid!')
          }
        } catch (err) {
          console.error('Upload error:', err)
          this.hideLoadingOverlay()
          alert('Audio upload failed: ' + err.message)
        }
      }
    }
    input.click()
  }

  _defineConverters() {
    const editor = this.editor
    const conversion = editor.conversion

    // Upcast: HTML to model
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        attributes: {
          'data-type': 'single-audio',
        },
      },
      model: (viewElement, { writer }) => {
        const src = viewElement.getAttribute('src') || ''
        const type = viewElement.getAttribute('type') || 'single'
        const text = viewElement.getAttribute('text') || ''
        const id = viewElement.getAttribute('id')

        const attrs = { text, src, type }
        if (id) attrs.id = id

        return writer.createElement('textToSpeech', attrs)
      },
    })

    // Upcast for legacy <text-to-speech> elements (optional, keep for backward compatibility)
    conversion.for('upcast').elementToElement({
      view: {
        name: 'text-to-speech',
      },
      model: (viewElement, { writer }) => {
        const text = viewElement.getAttribute('text') || ''
        const src = viewElement.getAttribute('src') || ''
        const type = viewElement.getAttribute('type') || ''
        const id = viewElement.getAttribute('id')

        const attrs = { text, src, type }
        if (id) attrs.id = id

        return writer.createElement('textToSpeech', attrs)
      },
    })

    // Data downcast: model to HTML (for saving)
    conversion.for('dataDowncast').elementToElement({
      model: 'textToSpeech',
      view: (modelElement, { writer }) => {
        const text = modelElement.getAttribute('text') || ''
        const src = modelElement.getAttribute('src') || ''
        const type = modelElement.getAttribute('type') || 'single'
        const id = modelElement.getAttribute('id')
        const attrs = {
          'data-type': 'single-audio',
          src: src,
          type: type,
          text: text,
        }
        if (id !== undefined && id !== null) {
          attrs.id = id
        }
        return writer.createEmptyElement('div', attrs)
      },
    })

    // Editing downcast: model to view (for editing)
    conversion.for('editingDowncast').elementToElement({
      model: 'textToSpeech',
      view: (modelElement, { writer }) => {
        const text = modelElement.getAttribute('text') || ''
        const src = modelElement.getAttribute('src') || ''

        const container = writer.createContainerElement('div', {
          class: 'tts-widget-container',
          draggable: 'false',
        })

        // Sürüklemeyi tamamen engellemek için event ekle
        editor.editing.view.document.on(
          'mousedown',
          (evt, data) => {
            const target = data.target
            if (
              target &&
              target.hasClass &&
              target.hasClass('tts-widget-container')
            ) {
              data.preventDefault()
            }
          },
          { priority: 'high' },
        )
        editor.editing.view.document.on(
          'dragstart',
          (evt, data) => {
            const target = data.target
            if (
              target &&
              target.hasClass &&
              target.hasClass('tts-widget-container')
            ) {
              data.preventDefault()
            }
          },
          { priority: 'high' },
        )

        // Create a placeholder that will be replaced with React component
        const placeholder = writer.createUIElement(
          'div',
          {
            class: 'tts-react-placeholder',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)
            // DOM'a da draggable=false ekle
            domElement.setAttribute('draggable', 'false')

            // Mount React component
            setTimeout(() => {
              try {
                const root = createRoot(domElement)
                root.render(
                  React.createElement(TextToSpeech, {
                    text,
                    type: 'single',
                    src: src,
                  }),
                )

                // Store root for cleanup
                editor.plugins
                  .get('TextToSpeechPlugin')
                  .mountedRoots.set(domElement, root)
              } catch (error) {
                console.error('Error rendering TextToSpeech component:', error)
                // Fallback: show simple button
                domElement.innerHTML = `
                  <button class="tts-fallback-button" onclick="alert('TextToSpeech component not available')">
                    🔊 Play Audio
                  </button>
                `
              }
            }, 0)

            return domElement
          },
        )

        writer.insert(writer.createPositionAt(container, 0), placeholder)

        // Widget olarak dönüştür
        return toWidget(container, writer, {
          label: 'Text to Speech Audio',
          hasSelectionHandle: true,
          draggable: false, // Sürüklenemez olsun
        })
      },
    })

    // src attribute'u değişince view'da da güncellensin
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'src',
      view: 'src',
    })
    conversion.for('dataDowncast').attributeToAttribute({
      model: 'src',
      view: 'src',
    })
  }

  _setupColumnObserver() {
    const editor = this.editor

    // Listen for changes in the document
    editor.model.document.on('change:data', () => {
      // Add TTS buttons to columns that don't have them
      this._addTTSButtonsToColumns()
    })

    // Also run on initial content load
    editor.on('ready', () => {
      this._addTTSButtonsToColumns()
    })
  }

  _addTTSButtonsToColumns() {
    const editor = this.editor
    const root = editor.model.document.getRoot()

    // Find all columns in the document
    const columns = []

    const findColumns = element => {
      if (element.name === 'column') {
        columns.push(element)
      }

      if (element.getChildren) {
        for (const child of element.getChildren()) {
          if (child.is && child.is('element')) {
            findColumns(child)
          }
        }
      }
    }

    findColumns(root)

    // Add TTS functionality to columns that have text but no TTS element
    columns.forEach(column => {
      const hasTextToSpeech = Array.from(column.getChildren()).some(
        child => child.name === 'textToSpeech',
      )

      if (!hasTextToSpeech) {
        const text = extractTextFromModelElement(column)
        if (text && text.length > 10) {
          // Only add if there's substantial text
          // Add TTS button to view
          this._addTTSButtonToColumnView(column, text)
        }
      }
    })
  }

  _addTTSButtonToColumnView(columnModel, text) {
    const editor = this.editor

    // Get the view element for this model column
    const columnView = editor.editing.mapper.toViewElement(columnModel)
    if (!columnView) return

    // Check if TTS button already exists
    const existingButton = Array.from(columnView.getChildren()).find(
      child => child.hasClass && child.hasClass('tts-column-button'),
    )
    if (existingButton) return

    // Dinamik src desteği
    let src = ''
    if (columnModel.getAttribute) {
      src = columnModel.getAttribute('src') || ''
    }

    setTimeout(() => {
      const domElement = editor.editing.view.domConverter.mapViewToDom(
        columnView,
      )
      if (!domElement) return

      // Create button container
      const buttonContainer = document.createElement('div')
      buttonContainer.className = 'tts-column-button-container'
      buttonContainer.style.cssText = `
        position: relative;
        display: inline-block;
        margin: 5px 0;
        z-index: 1000;
      `

      // Insert at the beginning of the column
      domElement.insertBefore(buttonContainer, domElement.firstChild)

      // Mount React component or fallback
      if (TextToSpeech) {
        try {
          const root = createRoot(buttonContainer)
          root.render(
            React.createElement(TextToSpeech, {
              text,
              size: 'small', // If component supports size prop
              type: 'single',
              src:
                src ||
                'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
            }),
          )

          this.mountedRoots.set(buttonContainer, root)
        } catch (error) {
          console.error('Error rendering column TTS button:', error)
          this._createFallbackButton(buttonContainer, text)
        }
      } else {
        this._createFallbackButton(buttonContainer, text)
      }
    }, 0)
  }

  _createFallbackButton(container, text) {
    container.innerHTML = `
      <button class="tts-fallback-button" style="
        background: #f0f0f0;
        border: 1px solid #ccc;
        border-radius: 4px;
        padding: 4px 8px;
        font-size: 12px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 4px;
      ">
        🔊 <span>Read Aloud</span>
      </button>
    `

    const button = container.querySelector('button')
    button.addEventListener('click', () => {
      // Fallback: use browser's speech synthesis
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text)
        speechSynthesis.speak(utterance)
      } else {
        alert('Speech synthesis not supported in this browser')
      }
    })
  }

  destroy() {
    // Cleanup mounted React components
    for (const [domElement, root] of this.mountedRoots) {
      try {
        root.unmount()
      } catch (error) {
        console.error('Error unmounting React component:', error)
      }
    }
    this.mountedRoots.clear()

    // Cleanup loading overlay
    const overlay = document.querySelector('.tts-loading-overlay')
    if (overlay) {
      overlay.remove()
    }

    // Cleanup injected hover styles
    const hoverStyleTag = document.getElementById('change-audio-hover-styles')
    if (hoverStyleTag) {
      hoverStyleTag.remove()
    }

    super.destroy()
  }
}

// Export for easier importing
export { TextToSpeechPlugin }
